import React, { FC, useRef, useState } from 'react';
import Slider from 'react-slick';
import styled from 'styled-components';
import { useDevice } from '../../../constants/device/device';
import media from '../../../constants/media';
import theme from '../../../constants/theme';
import { ReactComponent as ArrowRight } from '../../../icons/arrow-right-2.svg';
import IArticleListItem from '../../../interfaces/articles/articleListItem';
import IconButton from '../../iconButton';
import NewsCard from '../newsCard';

interface Props {
	articles: IArticleListItem[];
}

const SliderWrapper = styled.div`
	position: relative;
	margin: 18px 30px 0 -8px;

	.slick-list {
		overflow: visible;

		${media.desktopSmall`
			clip-path: inset(-100vw -100vw -100vw -8px);
		`};
	}

	.slick-track {
		display: flex;

		> div {
			height: auto;
			float: none;

			> div,
			> div > a {
				height: 100%;
			}
		}
	}

	${media.tablet`
		margin: 56px -8px 0;
	`};

	${media.desktopSmall`
        margin: 56px -12px 0;
    `};

	${media.desktopLarge`
        margin: 56px -16px 0;
    `};
`;

const ControlsWrapper = styled.div`
	position: absolute;
	z-index: 1;
	top: 48px;
	left: -56px;
`;

const ArrowPrev = styled(IconButton)`
	rotate: -180deg;
	margin-bottom: 36px;
`;

const ArrowNext = styled(IconButton)``;

const NewsCardWrapper = styled.div`
	padding-inline: 8px;
	height: 100%;
	display: flex !important;

	${media.desktopSmall`
		padding-inline: 12px;
	`}

	${media.desktopLarge`
		padding-inline: 16px;
	`}
`;

const Dots = styled.ul`
	display: flex;
	align-items: center;
	justify-content: center;
	margin: 28px 0 0 30px;
	padding: 0;
	list-style-type: none;

	${media.tablet`
		margin: 28px 0 0;
	`}
`;

interface DotProps {
	active: boolean;
}

const Dot = styled.li<DotProps>`
	margin-inline: 4px;
	width: ${({ active }) => (active ? '24px' : '8px')};
	height: 8px;
	border-radius: 4px;
	background: ${({ active }) => (active ? theme.colorsV2.grey5 : theme.colorsV2.grey4)};
	transition: all 0.4s ease;
`;

const NewsSlider: FC<Props> = ({ articles }) => {
	const sliderRef = useRef(null);
	const [activeIndex, setActiveIndex] = useState(0);
	const { currentDevice, smallDevice } = useDevice();

	const slidesToShow = () => {
		if (currentDevice === 'mobile') {
			return 1;
		}
		if (currentDevice === 'tablet') {
			return 2;
		}
		if (currentDevice === 'desktop') {
			return 3;
		}
		return 4;
	};

	const onPrev = () => {
		sliderRef.current?.slickPrev();
	};

	const onNext = () => {
		sliderRef.current?.slickNext();
	};

	const sliderSettings = {
		dots: false,
		arrows: false,
		infinite: false,
		speed: 400,
		slidesToShow: slidesToShow(),
		slidesToScroll: 1,
		beforeChange: (_: number, nextIndex: number) => {
			setActiveIndex(nextIndex);
		},
		draggable: smallDevice,
		autoplay: false,
	};

	return (
		<SliderWrapper>
			{!smallDevice && (
				<ControlsWrapper>
					<ArrowPrev disabled={activeIndex === 0} onClick={onPrev} type="secondary">
						<ArrowRight />
					</ArrowPrev>
					<ArrowNext
						disabled={activeIndex === articles.length - slidesToShow()}
						onClick={onNext}
						type="secondary">
						<ArrowRight />
					</ArrowNext>
				</ControlsWrapper>
			)}
			<Slider {...sliderSettings} ref={sliderRef}>
				{articles.map((card, index) => (
					<NewsCardWrapper key={index}>
						<NewsCard article={card} />
					</NewsCardWrapper>
				))}
			</Slider>
			{smallDevice && (
				<Dots>
					{[...Array(articles.length + 1 - slidesToShow())].map((_, index) => (
						<Dot key={index + 1} active={index === activeIndex} />
					))}
				</Dots>
			)}
		</SliderWrapper>
	);
};

export default NewsSlider;
